// client/src/components/admin/PendingUserManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PendingUserManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchPendingUsers = async () => {
      try {
        const res = await axios.get('/api/admin/pending-users');
        setPendingUsers(res.data.pendingUsers);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch pending users');
        setLoading(false);
      }
    };

    fetchPendingUsers();
  }, []);

  const handleApprove = async (userId) => {
    try {
      setActionInProgress(true);
      const res = await axios.put(`/api/admin/approve-user/${userId}`);
      
      // Remove the approved user from the list
      setPendingUsers(pendingUsers.filter(user => user._id !== userId));
      setSuccessMessage('User approved successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve user');
    } finally {
      setActionInProgress(false);
    }
  };

  const openRejectModal = (user) => {
    setSelectedUser(user);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setSelectedUser(null);
    setRejectionReason('');
  };

  const handleReject = async () => {
    if (!selectedUser) return;
    
    try {
      setActionInProgress(true);
      await axios.put(`/api/admin/reject-user/${selectedUser._id}`, {
        reason: rejectionReason
      });
      
      // Remove the rejected user from the list
      setPendingUsers(pendingUsers.filter(user => user._id !== selectedUser._id));
      
      // Close the modal
      closeRejectModal();
      
      setSuccessMessage('User rejected successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject user');
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading pending registrations...</div>;
  }

  return (
    <div className="admin-container">
      <h2 className="admin-page-title">Pending Registrations</h2>
      
      {error && <div className="alert-danger">{error}</div>}
      {successMessage && <div className="alert-success">{successMessage}</div>}
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Users Awaiting Approval</h3>
        </div>
        <div className="admin-card-body">
          {pendingUsers.length === 0 ? (
            <div className="admin-no-data-message">
              <p>No pending registrations to approve.</p>
            </div>
          ) : (
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Name</th>
                    <th style={{ width: '25%' }}>Email</th>
                    <th style={{ width: '12%' }}>Role</th>
                    <th style={{ width: '15%' }}>Organization</th>
                    <th style={{ width: '15%' }}>Registration Date</th>
                    <th style={{ width: '18%', minWidth: '170px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map(user => (
                    <tr key={user._id}>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>{user.organization ? user.organization.name : 'N/A'}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="admin-action-buttons">
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleApprove(user._id)}
                            disabled={actionInProgress}
                          >
                            Approve
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => openRejectModal(user)}
                            disabled={actionInProgress}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Reject Registration</h3>
              <button className="admin-modal-close-btn" onClick={closeRejectModal}>&times;</button>
            </div>
            <div className="admin-modal-body">
              <p>
                Are you sure you want to reject the registration for 
                <strong> {selectedUser.firstName} {selectedUser.lastName}</strong>?
              </p>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="rejectionReason">Reason for Rejection (Optional)</label>
                <textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a reason for rejection..."
                  rows="4"
                  className="admin-form-input"
                ></textarea>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button 
                className="btn btn-light"
                onClick={closeRejectModal}
                disabled={actionInProgress}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleReject}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Reject Registration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingUserManagement;